import * as React from 'react';
import { split } from 'canvas-hypertxt';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Props for the `TextSignature` component.
 */
export interface TextSignatureProps {
  /**
   * The text value to be rendered inside the canvas.
   */
  value: string;

  /**
   * Additional CSS class names to style the canvas element.
   */
  className?: string;

  /**
   * The name of the font to use for rendering the text.
   * Defaults to 'Hurricane'.
   */
  fontName?: string;

  /**
   * The font size in pixels for rendering the text.
   * Defaults to 64.
   */
  fontSize?: number;

  /**
   * The URL of the font file to load for rendering the text.
   * Defaults to a hosted 'Hurricane' font URL.
   */
  fontUrl?: string;

  style?: React.CSSProperties;
}

export const TextSignature = React.forwardRef<
  HTMLCanvasElement,
  TextSignatureProps
>(function TextSignature(props, canvasRefProp) {
  const {
    fontName = 'Hurricane',
    fontUrl = 'https://fonts.gstatic.com/s/hurricane/v7/pe0sMIuULZxTolZ5YldCBfe_Kdxicw.woff2',
    fontSize = 60,
    value,
    ...rest
  } = props;
  const [googleFont, setGoogleFont] = React.useState<FontFace | null>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  React.useEffect(() => {
    assignRef(canvasRefProp, canvasRef.current);

    async function loadGoogleFont() {
      const font = new FontFace(fontName, `url(${fontUrl})`);
      await font.load();

      document.fonts.add(font);
      return font;
    }

    loadGoogleFont()
      .then((f) => {
        setGoogleFont(f);
      })
      .catch((error) => {
        console.error('Failed to load font:', error);
      });
  }, [fontName, fontUrl, canvasRefProp]);

  React.useEffect(() => {
    if (!canvasRef.current || !googleFont) return;

    const canvas = canvasRef.current;
    const ctx = canvasRef.current.getContext('2d');

    if (!ctx) return;

    // Always render at fixed 800×492 resolution internally.
    // CSS handles visual scaling to fit the container.
    const REFERENCE_W = 800;
    const REFERENCE_H = 492;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = REFERENCE_W * ratio;
    canvas.height = REFERENCE_H * ratio;
    ctx.scale(ratio, ratio);

    // Use fixed reference dimensions for text layout
    const padding = 16;
    const width = REFERENCE_W - 2 * padding;
    const height = REFERENCE_H - 2 * padding;

    ctx.font = `${fontSize}px ${fontName}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = 'black';

    if (!value || value.trim() === '') return;

    const lineHeight = fontSize * 1.1;
    const lines = split(ctx, value, ctx.font, width, true);

    const textHeight = lines.length * lineHeight;
    let y = padding + (height - textHeight) / 2;

    for (const line of lines) {
      ctx.fillText(line, REFERENCE_W / 2, y);
      y += lineHeight;
    }
  }, [value, canvasRef, googleFont]);

  const styles = {
    width: 400,
    height: 246,
    ...(props.style ?? {}),
  };

  return (
    <div
      {...rest}
      className={cn('relative flex-1 bg-white', props.className)}
      style={styles}>
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
    </div>
  );
});

function assignRef<T>(ref: React.ForwardedRef<T>, value: T) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref != null) {
    ref.current = value;
  }
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
